import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import TopoCanvas from "@/components/site/TopoCanvas";
import { BorderBeam } from "@/components/ui/border-beam";
import PhotoGrid from "@/galleries/photo-grid";
import VideoShowcase from "@/galleries/video-showcase";
import PortfolioWorlds from "@/galleries/portfolio-worlds";
import SpecCards from "@/galleries/spec-cards";

const TABS = [
  { id: "photos", label: "Photos", Component: PhotoGrid },
  { id: "videos", label: "Videos", Component: VideoShowcase },
  { id: "work", label: "Work", Component: PortfolioWorlds },
  { id: "services", label: "Services", Component: SpecCards },
];

export default function GalleriesPage() {
  const [activeId, setActiveId] = useState(TABS[0].id);
  const [hovered, setHovered] = useState(null);
  const navigate = useNavigate();
  const Active = TABS.find((t) => t.id === activeId)?.Component ?? PhotoGrid;
  const highlight = hovered || activeId;

  return (
    <div className="App grain relative min-h-screen bg-paper">
      <TopoCanvas variant="paper" />

      <header className="sticky top-0 z-20 border-b border-hairline bg-paper/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-5 py-4 md:px-8">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="flex items-center gap-2.5 font-serif text-xl tracking-tight text-ink"
          >
            <img
              src="/logo-icon.png"
              alt="Zigler Designs owl mark"
              className="h-9 w-9 object-contain"
            />
            <span>
              <span className="text-pine">Zigler</span> Designs
            </span>
            <span className="ml-1 font-mono text-[10px] uppercase tracking-[0.16em] text-ink-dim">
              / Galleries
            </span>
          </button>

          <nav
            className="relative flex items-center gap-1 overflow-x-auto rounded-full border border-hairline bg-ink/5 px-1.5 py-1.5"
            onMouseLeave={() => setHovered(null)}
          >
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onMouseEnter={() => setHovered(tab.id)}
                onClick={() => setActiveId(tab.id)}
                className="relative whitespace-nowrap px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.16em]"
              >
                {highlight === tab.id && (
                  <motion.span
                    layoutId="gallery-nav-pill"
                    className="absolute inset-0 overflow-hidden rounded-full bg-pine"
                    transition={{ type: "spring", stiffness: 380, damping: 32 }}
                  >
                    {/* Traveling border signals the true selected tab —
                        distinct from the pine fill, which also previews on
                        hover. Only renders for the actual active tab. */}
                    {tab.id === activeId && (
                      <BorderBeam
                        size={28}
                        duration={5}
                        borderWidth={1.5}
                        colorFrom="#F7F4EE"
                        colorTo="#A13F2C"
                      />
                    )}
                  </motion.span>
                )}
                <span
                  className={`relative z-10 transition-colors duration-200 ${
                    highlight === tab.id ? "text-paper" : "text-ink-dim"
                  }`}
                >
                  {tab.label}
                </span>
              </button>
            ))}
          </nav>
        </div>
      </header>

      <main className="relative z-10">
        <Active />
      </main>
    </div>
  );
}
